import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, LogIn, Search, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useProfileSwitch } from "@/contexts/ProfileSwitchContext";
import { debug } from "@/lib/debug";
import { getTierDisplayName } from "@/lib/tierUtils";
import { cn } from "@/lib/utils";

interface Candidate {
  user_id: string;
  email: string;
  display_name: string | null;
  subscription_tier: string;
  is_admin: boolean;
}

/**
 * Every account, fetched once per page load rather than per menu open.
 *
 * get-admin-users pages through all of auth.users, all of user_credits and all of profiles on
 * every single call — six or so round trips server-side whatever perPage is set to. Radix
 * unmounts the dropdown's contents when it closes, so without this cache the whole list would
 * be refetched every time somebody opened the profile menu.
 */
let accountCache: Candidate[] | null = null;

/** One call is enough for this project's ~1,200 accounts; the loop below covers growth. */
const PAGE_SIZE = 1000;
const MAX_PAGES = 10;

/**
 * Accounts that can actually be switched into, first.
 *
 * get-admin-users sorts admins to the top, which is right for the admin panel and exactly
 * wrong here: the menu opened on six greyed rows nobody can click before reaching a single
 * usable account. Admins stay in the list, so filtering for one still shows the reason it is
 * refused, but they go to the bottom. Stable, so the server's most-recent-first ordering
 * survives inside each group.
 */
export function membersFirst(accounts: Candidate[]): Candidate[] {
  return [...accounts.filter((a) => !a.is_admin), ...accounts.filter((a) => a.is_admin)];
}

/** Name or email, case-insensitive. Pure, so it can be tested without a DOM. */
export function filterAccounts(accounts: Candidate[], term: string): Candidate[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return accounts;
  return accounts.filter(
    (a) =>
      a.email?.toLowerCase().includes(needle) ||
      a.display_name?.toLowerCase().includes(needle),
  );
}

function initials(name: string | null, email: string): string {
  const source = name?.trim() || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

/**
 * The account picker inside the profile menu.
 *
 * Two states, never both:
 *   switched     — one item, "Back to <the admin's email>", first in the menu
 *   not switched — the scrollable list of accounts, admins only
 *
 * The list is populated as soon as the menu opens. An earlier version showed nothing until you
 * typed two characters, which made it a search box rather than something you could browse. The
 * filter above it stays, because scrolling is not a way to find one account among twelve
 * hundred — but nothing has to be typed to see the list.
 *
 * While switched, the account in the browser is the member, who is not an admin, so the list is
 * correctly gone: the server would refuse to build it. Only the way back remains.
 */
/**
 * The rows themselves, split out from the state around them.
 *
 * Separate because it is the part worth asserting on — tier labels, admins listed but refused,
 * the scroll container — and it is pure, so renderToStaticMarkup can check all of it. The
 * suite runs on `environment: "node"` (vitest.config.ts) and jsdom will not load under this
 * Node version, so a component that only renders once its effect has fetched cannot be unit
 * tested at all. Anything that genuinely needs the effect is covered by the e2e in a browser.
 */
export function AccountRows({
  accounts,
  pending,
  onPick,
}: {
  accounts: Candidate[];
  pending: string | null;
  onPick: (candidate: Candidate) => void;
}) {
  return (
    <div className="p-1 space-y-0.5">
      {accounts.map((candidate) => {
        // Admins are listed but not offered. The server refuses them outright, and a greyed
        // row with a reason on it explains that better than a toast after the click does.
        const blocked = candidate.is_admin;
        const name = candidate.display_name?.trim() || candidate.email;
        return (
          <button
            key={candidate.user_id}
            type="button"
            role="option"
            aria-selected={false}
            aria-disabled={blocked}
            disabled={blocked || !!pending}
            onClick={() => onPick(candidate)}
            title={blocked ? "Admin accounts cannot be switched into" : `Sign in as ${candidate.email}`}
            className={cn(
              "group w-full flex items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left outline-none transition-colors",
              blocked
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-white/10 focus:bg-white/10 disabled:opacity-60",
            )}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-[10px] font-semibold">
              {pending === candidate.user_id
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : initials(candidate.display_name, candidate.email)}
            </span>

            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium leading-tight">{name}</span>
              {/* Only when there is a name to sit above it. Without one the email IS the
                  title, and the old fallback printed the tier here as well as in the chip. */}
              {candidate.display_name?.trim() && (
                <span className="block truncate text-[10px] leading-tight opacity-55">
                  {candidate.email}
                </span>
              )}
            </span>

            {/* The database tier names are offset from the ones members see — basic is
                "Free Plan", solopreneur is "Starter", reseller is "Solopreneur" — so printing
                the raw column would show an admin a different plan name than the one on the
                member's own billing page. */}
            <span className="shrink-0 rounded-full border border-white/15 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide opacity-70">
              {blocked ? "Admin" : getTierDisplayName(candidate.subscription_tier)}
            </span>

            {!blocked && (
              <LogIn className="h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus:opacity-70" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function ProfileSwitcher() {
  const { isSwitched, originalEmail, canSwitch, switching, switchToUser, switchBack } = useProfileSwitch();

  const [accounts, setAccounts] = useState<Candidate[] | null>(accountCache);
  // Starts true when there is no cache, because the fetch begins on the very next tick. Left
  // at false, the first paint rendered "No accounts to show." before the skeleton replaced it.
  const [loading, setLoading] = useState(!accountCache);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const collected: Candidate[] = [];
      for (let page = 1; page <= MAX_PAGES; page++) {
        const { data, error } = await supabase.functions.invoke("get-admin-users", {
          body: { page, perPage: PAGE_SIZE, search: "", tierFilter: "" },
        });
        if (error) {
          debug.error("[ProfileSwitcher] could not load accounts:", error.message);
          setFailed(true);
          return;
        }
        const batch = (data?.users ?? []) as Candidate[];
        collected.push(...batch);
        if (batch.length < PAGE_SIZE || collected.length >= (data?.pagination?.totalCount ?? 0)) break;
      }
      const ordered = membersFirst(collected);
      accountCache = ordered;
      setAccounts(ordered);
    } finally {
      setLoading(false);
    }
  }, []);

  // Runs on mount, and mount is when the menu opens — Radix does not keep closed content
  // rendered. The cache means only the first open of a page load actually waits.
  useEffect(() => {
    if (!canSwitch || isSwitched || accounts) return;
    load();
  }, [canSwitch, isSwitched, accounts, load]);

  const visible = useMemo(() => filterAccounts(accounts ?? [], filter), [accounts, filter]);

  // Typing should show the top of the new results, not wherever the previous scroll left off.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [filter]);

  const handlePick = async (candidate: Candidate) => {
    setPending(candidate.user_id);
    const { error } = await switchToUser(candidate.user_id);
    if (error) {
      toast.error(error);
      setPending(null);
    }
    // On success the page reloads, so there is nothing to reset.
  };

  if (isSwitched) {
    return (
      <>
        <button
          type="button"
          onClick={switchBack}
          disabled={switching}
          className="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-sm font-medium bg-white/10 hover:bg-white/20 focus:bg-white/20 outline-none transition-colors disabled:opacity-60"
        >
          {switching
            ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            : <Undo2 className="h-4 w-4 shrink-0" />}
          <span className="truncate text-left">Back to {originalEmail ?? "your account"}</span>
        </button>
        <DropdownMenuSeparator className="bg-white/10" />
      </>
    );
  }

  if (!canSwitch) return null;

  return (
    <>
      <div className="px-2 pt-1 pb-2">
        <div className="flex items-baseline justify-between px-0.5 pb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
            Sign in as
          </span>
          {accounts && (
            <span className="text-[10px] tabular-nums opacity-50">
              {filter.trim() ? `${visible.length} of ${accounts.length}` : `${accounts.length} accounts`}
            </span>
          )}
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 opacity-50" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            // Radix's menus own the keyboard: printable keys drive typeahead and would move
            // focus to a menu item instead of reaching the field. Stopping propagation is what
            // makes an input inside a DropdownMenu usable at all.
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Filter by name or email"
            aria-label="Filter the list of accounts"
            className="w-full rounded-md border border-white/15 bg-black/20 py-1.5 pl-8 pr-2 text-xs placeholder:text-[#f0c5e0]/40 outline-none transition-colors focus:border-white/35 focus:bg-black/30"
          />
        </div>

        <div
          ref={listRef}
          role="listbox"
          aria-label="Accounts you can sign in as"
          className="mt-1.5 max-h-72 overflow-y-auto overscroll-contain rounded-md border border-white/10 bg-black/10 [scrollbar-color:rgba(255,255,255,0.25)_transparent] [scrollbar-width:thin]"
        >
          {loading && (
            // Skeleton rows rather than a spinner: the list is about to be this shape, and the
            // menu does not jump when it arrives.
            <div className="p-1.5 space-y-1.5" aria-label="Loading accounts">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2.5 px-1.5 py-1">
                  <div className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-white/10" />
                  <div className="flex-1 space-y-1">
                    <div className="h-2.5 w-2/3 animate-pulse rounded bg-white/10" />
                    <div className="h-2 w-1/2 animate-pulse rounded bg-white/5" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {failed && !loading && (
            <div className="px-3 py-4 text-center">
              <p className="text-xs opacity-70">Could not load the account list.</p>
              <button
                type="button"
                onClick={load}
                className="mt-1.5 text-xs font-medium underline underline-offset-2 opacity-90 hover:opacity-100"
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !failed && visible.length === 0 && (
            <p className="px-3 py-4 text-center text-xs opacity-60">
              {accounts?.length ? "No account matches that." : "No accounts to show."}
            </p>
          )}

          {!loading && visible.length > 0 && (
            <AccountRows accounts={visible} pending={pending} onPick={handlePick} />
          )}
        </div>
      </div>
      <DropdownMenuSeparator className="bg-white/10" />
    </>
  );
}
